import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, ChevronUp, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ImageUpload from "@/components/ImageUpload";
import { TextField } from "@/pages/admin/adminShared";
import { TICKER_PAGES } from "@/components/SiteTicker";
import { fetchSiteContent } from "@/lib/siteContent";
import { infoPages } from "@/pages/infoContent";
import { homeDefaults } from "@/pages/homeDefaults";
import { aboutDefaults, becomeLegendDefaults, becomeHostDefaults, contactDefaults } from "@/pages/navPageDefaults";

const PAGE_LABELS = {
  "responsible-gaming": "Responsible Gaming",
  "terms": "Terms & Conditions",
};

function deepClone(o) { return JSON.parse(JSON.stringify(o)); }
const SECTION_PAGES = Object.keys(infoPages);

export default function ContentAdmin() {
  const [content, setContent] = useState({ home: deepClone(homeDefaults) });
  const [target, setTarget] = useState("home");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content").then((r) => {
      const saved = r.data || {};
      setContent({ ...saved, home: { ...deepClone(homeDefaults), ...(saved.home || {}) } });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", { data: content });
      setContent({ ...data, home: { ...deepClone(homeDefaults), ...(data.home || {}) } });
      toast.success("Content published — changes are live");
      fetchSiteContent();
    } catch (e) { toast.error("Could not save content"); } finally { setSaving(false); }
  };

  const setKey = (key, val) => setContent((c) => ({ ...c, [key]: val }));
  const setPage = (slug, val) => setContent((c) => ({ ...c, pages: { ...(c.pages || {}), [slug]: val } }));

  const resetTarget = () => {
    setContent((c) => {
      const n = { ...c };
      if (target === "home") n.home = deepClone(homeDefaults);
      else if (SECTION_PAGES.includes(target)) { const p = { ...(n.pages || {}) }; delete p[target]; n.pages = p; }
      else delete n[target];
      return n;
    });
    toast.success("Reverted to default (remember to Publish)");
  };

  const targets = [
    { key: "home", label: "Home Page" },
    { key: "about", label: "About" },
    { key: "become-legend", label: "Become a Legend" },
    { key: "become-host", label: "Become a Host Venue" },
    { key: "contact", label: "Contact Us" },
    { key: "sponsors", label: "Sponsors" },
    { key: "hall-of-legends", label: "Hall of Legends" },
    { key: "gallery", label: "Gallery" },
    ...SECTION_PAGES.map((s) => ({ key: s, label: PAGE_LABELS[s] || s })),
  ];

  const renderEditor = () => {
    if (target === "home") return <HomeContentEditor value={content.home} onChange={(v) => setKey("home", v)} />;
    if (target === "about") return <AboutEditor value={{ ...aboutDefaults, ...(content.about || {}) }} onChange={(v) => setKey("about", v)} />;
    if (target === "become-legend") return <ApplyEditor value={{ ...becomeLegendDefaults, ...(content["become-legend"] || {}) }} onChange={(v) => setKey("become-legend", v)} />;
    if (target === "become-host") return <ApplyEditor value={{ ...becomeHostDefaults, ...(content["become-host"] || {}) }} onChange={(v) => setKey("become-host", v)} />;
    if (target === "contact") return <ContactEditor value={{ ...contactDefaults, ...(content.contact || {}) }} onChange={(v) => setKey("contact", v)} />;
    if (target === "hall-of-legends" || target === "gallery") return <HeroImageEditor value={content[target] || {}} onChange={(v) => setKey(target, v)} testId={`${target}-hero-upload`} />;
    if (target === "sponsors") return <SponsorsEditor value={content.sponsors} onChange={(v) => setKey("sponsors", v)} />;
    return <PageContentEditor value={content.pages?.[target] || deepClone(infoPages[target])} onChange={(v) => setPage(target, v)} />;
  };

  return (
    <div className="mt-6" data-testid="admin-content">
      <div className="flex flex-col md:flex-row gap-6">
        <div className="md:w-56 flex-shrink-0">
          <div className="bg-navy-paper border border-slate-800 p-2 sticky top-28">
            {targets.map((t) => (
              <button key={t.key} onClick={() => setTarget(t.key)} data-testid={`content-target-${t.key}`}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${target === t.key ? "bg-gold text-navy font-semibold" : "text-slate-300 hover:bg-white/5"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <p className="text-slate-400 text-sm">Edit the content below, then <span className="text-gold">Publish</span> to make it live on the site.</p>
            <div className="flex gap-2">
              <Button onClick={resetTarget} variant="outline" className="rounded-none border-slate-700 text-slate-300 hover:bg-white/5 text-xs uppercase tracking-widest" data-testid="content-reset">Reset to default</Button>
              <Button onClick={save} disabled={saving} className="bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest text-xs" data-testid="content-save">{saving ? "Publishing…" : "Publish"}</Button>
            </div>
          </div>
          {renderEditor()}
        </div>
      </div>
    </div>
  );
}

function HomeContentEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setStat = (i, k, v) => { const stats = value.stats.map((s, idx) => idx === i ? { ...s, [k]: v } : s); set("stats", stats); };
  const setCta = (i, k, v) => { const cta = value.cta.map((s, idx) => idx === i ? { ...s, [k]: v } : s); set("cta", cta); };
  const ticker = value.ticker || { enabled: false, speed: 30, items: ["", "", "", ""] };
  const setTicker = (k, v) => set("ticker", { ...ticker, [k]: v });
  const togglePage = (p, on) => setTicker("pages", on ? [...new Set([...(ticker.pages || []), p])] : (ticker.pages || []).filter((x) => x !== p));
  const setTickerItem = (i, v) => { const items = [0, 1, 2, 3].map((idx) => (idx === i ? v : (ticker.items || [])[idx] || "")); setTicker("items", items); };
  return (
    <div className="space-y-6" data-testid="home-content-editor">
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <h4 className="font-display text-gold font-bold">Hero</h4>
        <TextField label="Eyebrow (small line above heading)" value={value.hero_eyebrow} onChange={(v) => set("hero_eyebrow", v)} />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Heading line 1" value={value.hero_title_line1} onChange={(v) => set("hero_title_line1", v)} />
          <TextField label="Heading line 2 (gold)" value={value.hero_title_line2} onChange={(v) => set("hero_title_line2", v)} />
        </div>
        <TextField label="Subtitle" value={value.hero_subtitle} onChange={(v) => set("hero_subtitle", v)} textarea rows={3} />
        <ImageUpload label="Hero background image" value={value.hero_image} onChange={(url) => set("hero_image", url)} testId="home-hero-upload" />
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3" data-testid="home-ticker-editor">
        <div className="flex items-center justify-between">
          <h4 className="font-display text-gold font-bold">Home Page Scroll Bar</h4>
          <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={!!ticker.enabled} onChange={(e) => setTicker("enabled", e.target.checked)} data-testid="ticker-enabled" /> Show scroll bar on Home</label>
        </div>
        <p className="text-slate-500 text-xs">Appears at the very top of the page, under the menu bar. Fill up to 4 messages.</p>
        <div className="flex flex-wrap gap-x-5 gap-y-2" data-testid="ticker-pages">
          <span className="text-slate-400 text-xs uppercase tracking-widest">Also show on:</span>
          {TICKER_PAGES.map((p) => (
            <label key={p.path} className="flex items-center gap-2 text-slate-300 text-sm">
              <input type="checkbox" checked={(ticker.pages || []).includes(p.path)} onChange={(e) => togglePage(p.path, e.target.checked)} data-testid={`ticker-page-${p.path.slice(1)}`} /> {p.label}
            </label>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <TextField key={i} label={`Message ${i + 1}`} value={(ticker.items || [])[i] || ""} onChange={(v) => setTickerItem(i, v)} />
          ))}
        </div>
        <div className="max-w-xs">
          <TextField label="Scroll speed (seconds per loop — lower is faster)" value={ticker.speed} onChange={(v) => setTicker("speed", Number(v) || 30)} />
        </div>
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5">
        <h4 className="font-display text-gold font-bold mb-3">Stats Strip</h4>
        <div className="grid sm:grid-cols-2 gap-4">
          {value.stats.map((s, i) => (
            <div key={i} className="grid grid-cols-2 gap-2">
              <TextField label={`Stat ${i + 1} value`} value={s.n} onChange={(v) => setStat(i, "n", v)} />
              <TextField label={`Stat ${i + 1} label`} value={s.l} onChange={(v) => setStat(i, "l", v)} />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5">
        <h4 className="font-display text-gold font-bold mb-3">Bottom Cards</h4>
        <div className="space-y-4">
          {value.cta.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-2">
              <TextField label={`Card ${i + 1} title`} value={c.title} onChange={(v) => setCta(i, "title", v)} />
              <TextField label={`Card ${i + 1} description`} value={c.desc} onChange={(v) => setCta(i, "desc", v)} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PageContentEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setSection = (i, patch) => set("sections", value.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeSection = (i) => set("sections", value.sections.filter((_, idx) => idx !== i));
  const addSection = () => set("sections", [...value.sections, { h: "New Section", p: "" }]);
  const setListItem = (si, li, v) => setSection(si, { list: value.sections[si].list.map((x, idx) => idx === li ? v : x) });
  const addListItem = (si) => setSection(si, { list: [...(value.sections[si].list || []), ""] });
  const removeListItem = (si, li) => setSection(si, { list: value.sections[si].list.filter((_, idx) => idx !== li) });

  return (
    <div className="space-y-6" data-testid="page-content-editor">
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <TextField label="Page title" value={value.title} onChange={(v) => set("title", v)} />
        <TextField label="Subtitle" value={value.subtitle} onChange={(v) => set("subtitle", v)} textarea rows={2} />
      </div>
      {value.sections.map((s, i) => (
        <div key={i} className="bg-navy-paper border border-slate-800 p-5 space-y-3" data-testid={`content-section-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-gold text-xs uppercase tracking-widest">Section {i + 1}</span>
            <button onClick={() => removeSection(i)} className="text-slate-500 hover:text-accent" title="Remove section" data-testid={`remove-section-${i}`}><Trash2 className="w-4 h-4" /></button>
          </div>
          <TextField label="Heading" value={s.h} onChange={(v) => setSection(i, { h: v })} />
          <TextField label="Paragraph (leave blank if using bullets only)" value={s.p} onChange={(v) => setSection(i, { p: v })} textarea rows={3} />
          <div>
            <Label className="text-slate-300 text-xs">Bullet list</Label>
            <div className="space-y-2 mt-1">
              {(s.list || []).map((li, j) => (
                <div key={j} className="flex gap-2">
                  <Input value={li} onChange={(e) => setListItem(i, j, e.target.value)} className="rounded-none bg-navy border-slate-700 text-sm" data-testid={`bullet-${i}-${j}`} />
                  <button onClick={() => removeListItem(i, j)} className="text-slate-500 hover:text-accent flex-shrink-0"><X className="w-4 h-4" /></button>
                </div>
              ))}
              <Button onClick={() => addListItem(i)} variant="outline" className="rounded-none border-slate-700 text-slate-300 hover:bg-white/5 text-xs" data-testid={`add-bullet-${i}`}><Plus className="w-3 h-3 mr-1" /> Add bullet</Button>
            </div>
          </div>
          {s.table && <p className="text-slate-500 text-[11px] italic">This section also has a data table which is preserved automatically.</p>}
        </div>
      ))}
      <Button onClick={addSection} variant="outline" className="rounded-none border-gold/40 text-gold hover:bg-gold hover:text-navy text-xs uppercase tracking-widest" data-testid="add-section"><Plus className="w-4 h-4 mr-1" /> Add section</Button>
    </div>
  );
}

function HeroImageEditor({ value, onChange, testId }) {
  return (
    <div className="space-y-4" data-testid={`${testId}-editor`}>
      <ImageUpload label="Hero background image" value={value.hero_image} onChange={(url) => onChange({ ...value, hero_image: url })} testId={testId} />
      <p className="text-slate-500 text-xs">Leave empty to use the default image. Click Publish to apply.</p>
    </div>
  );
}

function AboutEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setCard = (i, k, v) => set("cards", value.cards.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  return (
    <div className="space-y-6" data-testid="about-content-editor">
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <h4 className="font-display text-gold font-bold">Header</h4>
        <TextField label="Page title" value={value.title} onChange={(v) => set("title", v)} />
        <TextField label="Subtitle" value={value.subtitle} onChange={(v) => set("subtitle", v)} textarea rows={2} />
        <ImageUpload label="Hero background image" value={value.hero_image} onChange={(url) => set("hero_image", url)} testId="about-hero-upload" />
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <h4 className="font-display text-gold font-bold">Story</h4>
        <TextField label="Eyebrow" value={value.story_eyebrow} onChange={(v) => set("story_eyebrow", v)} />
        <TextField label="Story heading" value={value.story_title} onChange={(v) => set("story_title", v)} />
        <TextField label="Paragraph 1" value={value.story_p1} onChange={(v) => set("story_p1", v)} textarea rows={3} />
        <TextField label="Paragraph 2" value={value.story_p2} onChange={(v) => set("story_p2", v)} textarea rows={3} />
        <ImageUpload label="Story image" value={value.story_image} onChange={(url) => set("story_image", url)} testId="about-story-upload" />
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5">
        <h4 className="font-display text-gold font-bold mb-3">Feature Cards</h4>
        <div className="space-y-4">
          {value.cards.map((c, i) => (
            <div key={i} className="grid sm:grid-cols-2 gap-2">
              <TextField label={`Card ${i + 1} title`} value={c.title} onChange={(v) => setCard(i, "title", v)} />
              <TextField label={`Card ${i + 1} description`} value={c.desc} onChange={(v) => setCard(i, "desc", v)} />
            </div>
          ))}
        </div>
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <h4 className="font-display text-gold font-bold">Bottom Call-to-action</h4>
        <TextField label="CTA heading" value={value.cta_title} onChange={(v) => set("cta_title", v)} />
        <TextField label="CTA text" value={value.cta_text} onChange={(v) => set("cta_text", v)} />
      </div>
    </div>
  );
}

function ApplyEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-6" data-testid="apply-content-editor">
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <TextField label="Page title" value={value.title} onChange={(v) => set("title", v)} />
        <TextField label="Subtitle" value={value.subtitle} onChange={(v) => set("subtitle", v)} textarea rows={2} />
        <TextField label="Intro paragraph" value={value.blurb} onChange={(v) => set("blurb", v)} textarea rows={4} />
        <ImageUpload label="Hero background image" value={value.image} onChange={(url) => set("image", url)} testId="apply-hero-upload" />
      </div>
    </div>
  );
}

function ContactEditor({ value, onChange }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  return (
    <div className="space-y-6" data-testid="contact-content-editor">
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <h4 className="font-display text-gold font-bold">Header</h4>
        <TextField label="Page title" value={value.title} onChange={(v) => set("title", v)} />
        <TextField label="Subtitle" value={value.subtitle} onChange={(v) => set("subtitle", v)} textarea rows={2} />
        <TextField label="Intro paragraph" value={value.blurb} onChange={(v) => set("blurb", v)} textarea rows={3} />
        <ImageUpload label="Hero background image" value={value.image} onChange={(url) => set("image", url)} testId="contact-hero-upload" />
      </div>
      <div className="bg-navy-paper border border-gold/30 p-5 space-y-3">
        <h4 className="font-display text-gold font-bold">Contact Details</h4>
        <TextField label="Email" value={value.email} onChange={(v) => set("email", v)} />
        <TextField label="Phone" value={value.phone} onChange={(v) => set("phone", v)} />
        <TextField label="Base / Location" value={value.base} onChange={(v) => set("base", v)} />
        <TextField label="Facebook URL (leave blank to hide)" value={value.facebook} onChange={(v) => set("facebook", v)} />
        <TextField label="Instagram URL (leave blank to hide)" value={value.instagram} onChange={(v) => set("instagram", v)} />
      </div>
    </div>
  );
}


function SponsorsEditor({ value, onChange }) {
  const list = value || [];
  const setItem = (i, k, v) => onChange(list.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const add = () => onChange([...list, { name: "", logo: "", url: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const copy = list.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    onChange(copy);
  };
  return (
    <div className="space-y-4" data-testid="sponsors-editor">
      <p className="text-slate-400 text-sm">Add sponsor / partner logos. They appear in the site footer and on event pages. Leave empty to hide the strip.</p>
      {list.map((s, i) => (
        <div key={i} className="bg-navy-paper border border-slate-800 p-5 space-y-3" data-testid={`sponsor-${i}`}>
          <div className="flex justify-between items-center">
            <span className="text-gold text-xs uppercase tracking-widest">Sponsor {i + 1}</span>
            <div className="flex items-center gap-2">
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move sponsor ${i + 1} up`} className="text-slate-500 hover:text-gold disabled:opacity-30 disabled:hover:text-slate-500" title="Move up" data-testid={`sponsor-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === list.length - 1} aria-label={`Move sponsor ${i + 1} down`} className="text-slate-500 hover:text-gold disabled:opacity-30 disabled:hover:text-slate-500" title="Move down" data-testid={`sponsor-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
              <button onClick={() => remove(i)} aria-label={`Remove sponsor ${i + 1}`} className="text-slate-500 hover:text-accent" data-testid={`remove-sponsor-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <TextField label="Name" value={s.name} onChange={(v) => setItem(i, "name", v)} />
          <div>
            <label className="text-slate-300 text-xs">Tier</label>
            <select value={s.tier || "supporting"} onChange={(e) => setItem(i, "tier", e.target.value)} className="w-full mt-1 rounded-none bg-navy border border-slate-700 text-slate-200 text-sm p-2" data-testid={`sponsor-tier-${i}`}>
              <option value="headline">Headline (large)</option>
              <option value="supporting">Supporting (small)</option>
            </select>
          </div>
          <TextField label="Website URL (optional)" value={s.url} onChange={(v) => setItem(i, "url", v)} />
          <ImageUpload label="Logo" value={s.logo} onChange={(url) => setItem(i, "logo", url)} testId={`sponsor-logo-${i}`} />
        </div>
      ))}
      <Button onClick={add} variant="outline" className="rounded-none border-gold/40 text-gold hover:bg-gold hover:text-navy text-xs uppercase tracking-widest" data-testid="add-sponsor"><Plus className="w-4 h-4 mr-1" /> Add sponsor</Button>
    </div>
  );
}
